/**
 * The maths behind swipe-to-reply, kept away from the DOM.
 *
 * One gesture, described as two decisions: which way the finger went, and how
 * far the bubble follows it. Distances are pointer pixels — what a PointerEvent
 * reports — and the pull that comes back is how far *left* the message has
 * moved, so the caller writes `translateX(-pull)` and nothing here has to know
 * about the sign convention of a transform.
 */

/** How far the bubble travels before letting go actually starts a reply. */
export const REPLY_TRIGGER_PX = 56;

/**
 * How far a finger has to go before the gesture is one thing or the other.
 * Below this the thread is still free to scroll, which is the common case: most
 * touches on a message are the start of a scroll that happens to land on one.
 */
export const SWIPE_SLOP_PX = 12;

/** Where the bubble stops following, however far the finger keeps going. */
const MAX_PULL_PX = 84;

/**
 * How much of the travel past the trigger the bubble actually makes. Past the
 * point where letting go would reply there is nothing left to promise, so the
 * bubble goes heavy under the finger rather than sliding off the screen.
 */
const RESISTANCE = 0.3;

/** Which way a gesture went, once it has gone far enough to say. */
export type SwipeAxis = 'none' | 'horizontal' | 'vertical';

/**
 * Locks the gesture to an axis, or leaves it undecided.
 *
 * Vertical wins ties and near-ties: the thread scrolls under every message, and
 * a reply that opened because a scroll drifted sideways is a worse mistake than
 * a swipe that has to be repeated a little straighter.
 */
export function swipeAxis(dx: number, dy: number): SwipeAxis {
  if (Math.abs(dy) >= SWIPE_SLOP_PX && Math.abs(dy) >= Math.abs(dx)) return 'vertical';
  if (Math.abs(dx) >= SWIPE_SLOP_PX && Math.abs(dx) > Math.abs(dy)) return 'horizontal';
  return 'none';
}

/**
 * How far the message has moved, for a finger that has travelled `dx`.
 *
 * Rightwards is nothing at all — the gesture is leftwards only, as in Telegram,
 * and dragging a bubble the other way must not leave it hanging off its own
 * side of the thread. The slop is taken off the front so the message starts
 * moving from where the gesture was recognised rather than jumping by 12px.
 */
export function replyPull(dx: number): number {
  const travel = -dx - SWIPE_SLOP_PX;
  if (travel <= 0) return 0;
  if (travel <= REPLY_TRIGGER_PX) return travel;
  return Math.min(MAX_PULL_PX, REPLY_TRIGGER_PX + (travel - REPLY_TRIGGER_PX) * RESISTANCE);
}

/** Whether letting go now replies. Also what lights the arrow up. */
export function isArmed(pull: number): boolean {
  return pull >= REPLY_TRIGGER_PX;
}

/**
 * How lit the arrow is, 0–1: it fades in over the pull and is fully there at
 * the moment the gesture becomes a reply.
 */
export function replyProgress(pull: number): number {
  return Math.min(1, pull / REPLY_TRIGGER_PX);
}
